/** The ONLY place a permission string is defined. Keep resources coarse (a domain, not a table) and
 * actions few: a new action is a product decision. */
export const statement = {
  dashboard: ["read"],
  organization: [
    "read",
    "update",
    "manage-members",
    "manage-roles",
    "manage-billing",
    "administer",
  ],
  platform: ["read", "manage"],
  facility: ["read", "create", "update", "manage-members", "manage-settings"],
  staff: ["read", "create", "update", "deactivate", "impersonate"],
  resident: ["read", "create", "update", "delete", "merge", "export"],
  "resident.profile": ["read", "update"],
  "resident.clinical": ["read", "update"],
  "resident.financials": ["read", "update"],
  "resident.documents": ["read", "update", "sign"],
  /** Who signs in for a resident (family links, invitations, the resident's own login). credentials
   * mints a login or temporary password: whoever reads one out can sign in as them. */
  portal: ["read", "manage", "credentials"],
  /** The family community. manage includes building settings, calling hours and residents' photo
   * consent. */
  community: ["read", "engage", "moderate", "manage"],
  /** `correct` (changing or voiding a charted dose) is apart so a nurse lead can hold it and a
   * floor manager cannot. */
  medication: ["read", "administer", "receive", "manage", "correct"],
  /** The care pass: laundry, room cleaning, bathing and supervision, round by round. `record` is
   * the floor's; `manage` builds the building's shifts and each resident's week. */
  carepass: ["read", "record", "manage"],
  activity: ["read", "manage"],
  incident: ["read", "manage"],
  // The front door. security is apart so the desk never decides who is banned.
  visitor: ["read", "manage", "security"],
  schedule: ["read", "manage"],
  /** Its own resource: booking an appointment must not grant managing the staff rota. */
  appointment: ["manage"],
  admission: ["read", "manage", "sign"],
  crm: ["read", "manage"],
  directory: ["read", "manage"],
  report: ["read", "export"],
  financial: ["read", "manage", "reconcile"],
  task: ["read", "manage"],
  note: ["read", "manage"],
  therapy: ["read", "chart", "cosign", "manage"],
  hiring: ["read", "manage"],
  hr: ["read", "manage"],
  training: ["read", "manage"],
  policy: ["read", "manage"],
  knowledgebase: ["read", "manage", "share", "audit"],
  /** `call` is the building's telephone: administrators and front desk roster only. */
  frontdesk: ["read", "call", "manage"],
  // Shelby, the sales assistant: her own resource, so giving admissions her screens is not giving
  // them the switchboard.
  salesagent: ["read", "manage"],
  fax: ["read", "send", "manage"],
  catalog: ["read", "manage"],
  settings: ["read", "manage"],
  nancy: ["read", "run", "configure"],
  menu: ["read", "manage"],
  messaging: ["use", "call", "record", "guests", "export"],
  amy: ["read", "work", "approve", "close"],
  olivia: ["read"],
  // David, the maintenance coordinator. read = work orders, the parts shop and his chat; manage =
  // file and work orders, keep the shop's count, ask for parts; approve = approve the spend.
  maintenance: ["read", "manage", "approve"],
} as const;

type Statement = typeof statement;
export type Resource = keyof Statement;
type ActionOf<R extends Resource> = Statement[R][number];

export type Permission = {
  [R in Resource]: `${R}:${ActionOf<R>}`;
}[Resource];

export const ALL_PERMISSIONS: readonly Permission[] = (
  Object.keys(statement) as Resource[]
).flatMap((resource) => statement[resource].map((action) => `${resource}:${action}` as Permission));

export function isPermission(value: string): value is Permission {
  return (ALL_PERMISSIONS as readonly string[]).includes(value);
}

export function splitPermission(permission: Permission): {
  resource: Resource;
  action: string;
} {
  const [resource, action] = permission.split(":") as [Resource, string];
  return { resource, action };
}

export function permissionsFor(resource: Resource, actions?: readonly string[]): Permission[] {
  const allowed = actions ?? statement[resource];
  return allowed.map((action) => `${resource}:${action}` as Permission);
}
